import TicketSection from 'app/components/TicketSection';
import TicketGroup from 'app/components/TicketSection/TicketGroup';
import { selectTicketSalePageData } from 'app/pages/TicketSalePage/slice/selectors';
import {
  EventCalendar,
  EventReservedTickets,
} from 'app/pages/TicketSalePage/slice/types';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Ticket, TicketTypeBundle } from 'types/TicketsState';
import {
  dateFormatForCalendar,
  getClosestAvailableDay,
  getTicketsForSpecificDate,
} from 'utils/calendar';
import {
  getItemsTree,
  isTicketType,
  isTicketTypeBundle,
} from '../getItemsTree';
import CalendarDates from './CalendarDates';
import { CalendarContext } from './calendarContext';
import { selectVendorFeatures } from 'store/vendorSlice/selectors';
import DateCalendarWrapper from './DateCalendarWrapper';
import CalendarLegend from './CalendarLegend';
import ApplyCodeSection from '../ApplyCodeSection';
import TicketTypeBundleSection from 'app/components/TicketTypeBundleSection';

const CalendarContainer: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { displayCalendarInsidePageLayout } = useSelector(selectVendorFeatures);
  const {
    selectedEvent: {
      tickets,
      ticketTypeBundles,
      ticketTypeGroups,
      dateTimeFormat: { timezone },
      calendar,
      reservedTickets,
    },
  } = useSelector(selectTicketSalePageData);
  const [selectedDate, setSelectedDate] = useState(
    getClosestAvailableDay(
      calendar as EventCalendar,
      reservedTickets as EventReservedTickets,
      timezone,
    ),
  );

  const handleDateChange = (day: DateTime) => setSelectedDate(day);

  const nonCalendarTickets = tickets.filter(ticket => !ticket.hasCalendar);
  let displayedTickets = selectedDate
    ? getTicketsForSpecificDate(calendar as EventCalendar, selectedDate)
        .map(element =>
          tickets.find(ticket => ticket.id === element.ticketTypeId),
        )
        .filter((el): el is Ticket => el !== undefined)
    : [];
  displayedTickets = [...displayedTickets, ...nonCalendarTickets];

  const displayedTicketBundles = getDisplayedTicketBundles(
    ticketTypeBundles || [],
    displayedTickets,
  );

  return (
    <CalendarContext.Provider
      value={
        selectedDate
          ? { selectedDate, setSelectedDate, handleDateChange }
          : null
      }
    >
      {selectedDate ? (
        <>
          {displayCalendarInsidePageLayout ? (
            <>
              <DateCalendarWrapper />
              <div className="px-[24px] py-[12px] flex flex-wrap justify-between gap-[12px]">
                <CalendarLegend />
              </div>
            </>
          ) : (
            <CalendarDates />
          )}

          <div className="p-[12px] sm:p-[24px] border-t border-neutral-40">
            <ApplyCodeSection />
          </div>

          {displayedTickets.length ? (
            <div className="border-t border-neutral-40">
              {getItemsTree(
                displayedTickets,
                displayedTicketBundles,
                ticketTypeGroups,
              ).map(item =>
                isTicketType(item) ? (
                  <TicketSection
                    key={`${item.id}-${selectedDate.toFormat(
                      dateFormatForCalendar,
                    )}`}
                    ticket={item}
                  />
                ) : isTicketTypeBundle(item) ? (
                  <TicketTypeBundleSection
                    key={`${item.id}-${selectedDate.toFormat(
                      dateFormatForCalendar,
                    )}`}
                    ticketTypeBundle={item}
                  />
                ) : (
                  <TicketGroup
                    key={`${item.id}-${selectedDate.toFormat(
                      dateFormatForCalendar,
                    )}`}
                    selectedDate={selectedDate}
                    tickets={displayedTickets}
                    bundles={displayedTicketBundles}
                    id={item.id}
                    name={item.name}
                  />
                ),
              )}
            </div>
          ) : null}
        </>
      ) : (
        <h2 className="p-[24px] border-b border-neutral-40">
          {t('ticketSale.thereAreNotTicketsAvailable')}
        </h2>
      )}
    </CalendarContext.Provider>
  );
};

export default CalendarContainer;

const getDisplayedTicketBundles = (
  ticketTypeBundles: TicketTypeBundle[],
  displayedTickets: Ticket[],
) => {
  const displayedTicketsIDs = displayedTickets.map(ticket => ticket.id);
  return ticketTypeBundles.filter(ticketTypeBundle =>
    ticketTypeBundle.items.every(item =>
      displayedTicketsIDs.includes(item.idTicketType),
    ),
  );
};
